import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { usePropertyListingsInfinite } from "@/hooks/usePropertyListings";
import { prefetchProperty } from "@/lib/prefetch-property";

interface InventoryStripProps {
  /** "hero" renders glass cards over the hero image; "light" renders on page background. */
  variant?: "hero" | "light";
  limit?: number;
  heading?: string;
  className?: string;
}

/**
 * Compact "From $X / night" inventory preview.
 * Surfaces real homes + real prices above the fold (hero) and mid-article (guides),
 * so visitors see inventory before they have to scroll or pick dates.
 */
const InventoryStrip = ({
  variant = "light",
  limit = 3,
  heading,
  className,
}: InventoryStripProps) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = usePropertyListingsInfinite({ pageSize: limit });
  const properties = (data?.pages?.[0] ?? []).slice(0, limit);

  const isHero = variant === "hero";

  if (isLoading) {
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${className ?? ""}`}>
        {Array.from({ length: limit }).map((_, i) => (
          <div
            key={i}
            className={`h-20 rounded-xl animate-pulse ${
              isHero ? "bg-white/15" : "bg-muted"
            }`}
          />
        ))}
      </div>
    );
  }

  if (properties.length === 0) return null;

  return (
    <div className={className}>
      {heading && (
        <h2
          className={`font-display text-xl md:text-2xl font-semibold mb-4 ${
            isHero ? "text-white" : "text-foreground"
          }`}
        >
          {heading}
        </h2>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {properties.map((p) => {
          const high = Number(p.price_per_night ?? 0);
          const low = Number(p.price_per_night_low ?? 0);
          const baseline = low > 0 ? Math.min(high, low) : high;
          return (
            <Link
              key={p.id}
              to={`/property/${p.slug}`}
              onMouseEnter={() => prefetchProperty(queryClient, p.slug)}
              onFocus={() => prefetchProperty(queryClient, p.slug)}
              className={`group flex items-center gap-3 rounded-xl p-2 pr-4 text-left transition-colors ${
                isHero
                  ? "bg-white/12 backdrop-blur-md ring-1 ring-white/25 hover:bg-white/20"
                  : "bg-card ring-1 ring-border/60 hover:ring-accent shadow-card"
              }`}
            >
              <img
                src={p.image}
                alt={p.title}
                loading="lazy"
                width={64}
                height={64}
                className="h-16 w-16 rounded-lg object-cover shrink-0"
              />
              <span className="min-w-0">
                <span
                  className={`block font-display text-sm font-semibold truncate ${
                    isHero ? "text-white" : "text-foreground"
                  }`}
                >
                  {p.title}
                </span>
                {baseline > 0 && (
                  <span
                    className={`block font-body text-xs ${
                      isHero ? "text-white/85" : "text-muted-foreground"
                    }`}
                  >
                    From{" "}
                    <span
                      className={`font-semibold ${isHero ? "text-white" : "text-foreground"}`}
                    >
                      ${Math.round(baseline).toLocaleString()}
                    </span>{" "}
                    / night
                  </span>
                )}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default InventoryStrip;
